import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useAppDispatch } from '../../hooks';

import { MandateActions } from '../../actions/mandate/mandate.actions';

import type { Mandate } from '../../interfaces/client/mandate.interface';

import StageChip from './StageChip';
import { deriveStage } from '../lib/mandate-helpers';

// =============================================================================
//  Inline stage picker — popover anchored to the StageChip on dashboard /
//  mandates list rows.  Clicking the chip opens the popover; selecting an
//  option dispatches `updateMandate` and closes.
//
//  Two reasons for the portal + manual positioning:
//    1. The popover must escape any table-overflow clipping.
//    2. The popover must overlay the chip without re-flowing the row.
// =============================================================================

const STAGE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'execution', label: 'Execution' },
  { value: 'due_diligence', label: 'Due Diligence' },
  { value: 'pre_listing', label: 'Pre-listing' },
  { value: 'implementation', label: 'Implementation' },
  { value: 'closing', label: 'Closing' },
  { value: 'reporting', label: 'Reporting' },
  { value: 'structuring', label: 'Structuring' },
  { value: 'field_work', label: 'Field Work' },
  { value: 'closed', label: 'Closed' }
];

interface Props {
  mandate: Mandate;
}

const InlineStagePicker = ({ mandate }: Props) => {
  const dispatch = useAppDispatch();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [saving, setSaving] = useState(false);

  // Anchor below the chip when opening.
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 4 + window.scrollY, left: rect.left + window.scrollX });
  }, [open]);

  // Close on outside click + Escape while open.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Clicks on the trigger or anywhere inside the popover keep it open.
      if (triggerRef.current?.contains(target)) return;
      if (target.closest('[data-v3-stage-popover]')) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const currentStage = mandate.stage || 'execution';

  const handlePick = (nextStage: string) => {
    if (nextStage === currentStage) {
      setOpen(false);
      return;
    }
    setSaving(true);
    dispatch(
      MandateActions.updateMandate(
        mandate.id as number | string,
        { stage: nextStage } as Partial<Mandate> as Mandate,
        () => {
          setSaving(false);
          setOpen(false);
        }
      )
    );
  };

  const portalRoot = typeof document !== 'undefined' ? document.getElementById('modal-root') : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="stage-picker-trigger"
        onClick={(e) => {
          // Don't fall through to the row click (which navigates to detail).
          e.stopPropagation();
          if (saving) return;
          setOpen((prev) => !prev);
        }}
        title="Change stage"
      >
        <StageChip stage={deriveStage(mandate)} />
      </button>

      {open &&
        pos &&
        portalRoot &&
        createPortal(
          <div
            data-v3="true"
            data-v3-stage-popover="true"
            className="stage-popover"
            style={{ top: pos.top, left: pos.left }}
            // Stop clicks inside the popover from bubbling out (e.g. row click).
            onClick={(e) => e.stopPropagation()}
          >
            {STAGE_OPTIONS.map((opt) => {
              const active = opt.value === currentStage;
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={active ? 'active' : ''}
                  onClick={() => handlePick(opt.value)}
                  disabled={saving}
                >
                  <StageChip stage={opt.label} />
                  {active && <span className="check">✓</span>}
                </button>
              );
            })}
          </div>,
          portalRoot
        )}
    </>
  );
};

export default InlineStagePicker;
